import {
  BlendIcon,
  CalendarCheckIcon,
  CalendarIcon,
  FlaskConicalIcon,
  LibraryBigIcon,
  LifeBuoyIcon,
  type LucideIcon,
  NotebookPenIcon,
  PackageIcon,
  SlidersHorizontalIcon,
  TrendingUpIcon,
  WheatIcon,
} from 'lucide-react';

export interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly description: string;
  readonly icon: LucideIcon;
}

/**
 * The routes reachable from the sidebar and the command palette.
 *
 * The order is an argument, not an accident: the two recipe surfaces pair up
 * top (yours, then everyone's), the brew loop follows in the order a brewer
 * lives it (brew → schedule → learn), then the stock pages a brewer touches
 * weekly (the cupboard, then the catalog that names what is in it), then the
 * profiles configured once and revisited rarely, and Support last.
 *
 * Settings is deliberately not here: the sidebar's account row already goes
 * there and now says so with a gear, and the palette carries its own Settings
 * command — a labelled row in this list was a third door to the same page.
 *
 * Support sits *beside* Settings rather than inside it (PLAN-SUPPORT). It was a
 * section of the settings page while it was one private thread; it gained a
 * public board that a signed-out visitor can read, and a page a stranger is
 * meant to find is not a section of the page where somebody changes their
 * units.
 *
 * Equipment and Water used to be two entries here. They are tabs of Profiles
 * now (PLAN-PROFILES P3): four of eleven entries were already profile
 * libraries, and adding mash and fermentation as their own would have made it
 * six of thirteen.
 *
 * Journal sits with Blending rather than with Batches because both are the
 * cellar rather than the kettle: a blend and a months-long mead are things that
 * happen to beer, or to something that is not beer, after the brew day is over.
 *
 * My Listings went the same way, which is why the top is a pair rather than a
 * trio: a brewer's own listings are a tab of the gallery now, and two entries
 * that both opened a directory of published recipes were a distinction only the
 * code cared about.
 */
export const NAV_ITEMS = [
  {
    to: '/recipes',
    label: 'My Recipes',
    description: 'Design and browse your recipes.',
    icon: FlaskConicalIcon,
  },
  {
    to: '/gallery',
    label: 'Recipe Gallery',
    description: 'Recipes brewers have published, and your own listings.',
    icon: LibraryBigIcon,
  },
  {
    to: '/batches',
    label: 'Batches',
    description: 'Brew days, measurements and actuals.',
    icon: CalendarCheckIcon,
  },
  {
    to: '/calendar',
    label: 'Calendar',
    description: 'Brew days, dry hops and cellar actions.',
    icon: CalendarIcon,
  },
  {
    to: '/insights',
    label: 'Insights',
    description: 'Your brewing, charted over time.',
    icon: TrendingUpIcon,
  },
  {
    to: '/blending',
    label: 'Blending',
    description: 'Barrel-aged beers, lambic and cellar blends.',
    icon: BlendIcon,
  },
  {
    to: '/journals',
    label: 'Journals',
    description: 'Mead, cider, sauces and anything else that ferments, written up as it goes.',
    icon: NotebookPenIcon,
  },
  {
    to: '/inventory',
    label: 'Inventory',
    description: 'What is in the cupboard, and what you need to buy.',
    icon: PackageIcon,
  },
  {
    to: '/ingredients',
    label: 'Ingredients',
    description: 'The malt, hop, yeast and misc catalog.',
    icon: WheatIcon,
  },
  {
    to: '/profiles',
    label: 'Profiles',
    description: 'Your kit, your water, and the mash and fermentation schedules you brew to.',
    icon: SlidersHorizontalIcon,
  },
  {
    to: '/support',
    label: 'Support',
    description: 'Ask us for help, and vote on what we build next.',
    icon: LifeBuoyIcon,
  },
] as const satisfies readonly NavItem[];
